import HomeAction from 'home/actions/HomeAction'
import LayerDto from 'home/dto/LayerDto'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import ReferencialAction from '../../../referencial/action/ReferencialAction'
import DtoSandreCode from '../../../referencial/dto/DtoSandreCode'
import DtoPublicPicture from '../../../station/dto/DtoPublicPicture'
import { getSetting } from '../../../utils/SettingUtils'
import AdministrationAction from '../../actions/AdministrationAction'
import { NAME_LOGIN_PICTURE, NAME_MENU_PICTURE } from '../../constants/AdministrationConstants'
import SieauParameterDto from '../../dto/SieauParameterDto'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import { Tab, Tabs } from '@mui/material'
import SettingsPanel from './panels/SettingsPanel'
import SecurityPanel from './panels/SecurityPanel'
import SupportPanel from './panels/SupportPanel'
import ApplicationParametersPanel from './panels/ApplicationParametersPanel'
import useTitle from '../../../utils/customHook/useTitle'
import useActions from '../../../utils/customHook/useActions'
import DtoUser from '../user/dto/DtoUser'
import { compact } from 'lodash'

const SETTINGS_INDEX = 0
const SECURITY_INDEX = 1
const SUPPORT_INDEX = 2
const APP_PARAMS_INDEX = 3

const SettingApp = ({}) => {
    const {
        applicationSettingsStore,
        sandreCodes,
        menuPicture,
        loginPicture,
        themeLayers,
        contributors,
        accountUser,
    } = useSelector(store => ({
        applicationSettingsStore: store.AdministrationReducer.applicationSettings,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        menuPicture: store.AdministrationReducer.menuPicture,
        loginPicture: store.AdministrationReducer.loginPicture,
        themeLayers: store.HomeReducer.themeLayers,
        contributors: store.ContributorReducer.contributors,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [applicationSettings, setApplicationSettings] = useState(applicationSettingsStore)
    const [files, setFiles] = useState([])
    const [tab, setTab] = useState(SETTINGS_INDEX)
    const [disabled, setDisabled] = useState(true)

    const getNameSettings = (setting) => {
        return setting === NAME_MENU_PICTURE ?
            getSetting(applicationSettingsStore, NAME_MENU_PICTURE):
            getSetting(applicationSettingsStore, NAME_LOGIN_PICTURE)
    }

    useEffect(() => {
        dispatch(HomeAction.setHelpLink('administration', '93'))
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
        if (!menuPicture.length) {
            dispatch(AdministrationAction.fetchPublicPicture(getNameSettings(NAME_MENU_PICTURE), NAME_MENU_PICTURE))
        }
        if (!loginPicture.length) {
            dispatch(AdministrationAction.fetchPublicPicture(getNameSettings(NAME_LOGIN_PICTURE), NAME_LOGIN_PICTURE))
        }
        if (!themeLayers.length) {
            dispatch(HomeAction.fetchThemeLayers())
        }
        if (!contributors.length) {
            dispatch(ContributorAction.fetchContributors())
        }
    }, [dispatch])

    useTitle(() => [{
        title: i18n.administration,
        href: 'administration',
    }, {
        title: i18n.settings,
        href: 'administration',
    }], [])

    useActions(() => {
        if (accountUser.consultant === '1') return {}
        return (disabled) ?
            {
                edit: () => {
                    setDisabled(false)
                },
            } :
            {
                cancel: () => {
                    setApplicationSettings(applicationSettingsStore)
                    setDisabled(true)
                },
                save: () => {
                    if (files.length > 0) {
                        dispatch(AdministrationAction.addPublicFiles(files)).then(() => {
                            setFiles([])
                            const menuPictureFile = files.find(f => f.stationType === NAME_MENU_PICTURE)
                            const loginPictureFile = files.find(f => f.stationType === NAME_LOGIN_PICTURE)
                            Promise.all(compact([
                                menuPicture && dispatch(AdministrationAction.fetchPublicPicture(menuPictureFile?.name, NAME_MENU_PICTURE)),
                                loginPicture && dispatch(AdministrationAction.fetchPublicPicture(loginPictureFile?.name, NAME_LOGIN_PICTURE)),
                            ])).then(() => {
                                dispatch(AdministrationAction.fetchApplicationSettings())
                            })
                        })
                    }
                    const appSettings = applicationSettings.filter(e => {
                        return e.value !== getSetting(applicationSettingsStore, e.parameter)
                    })
                    if (appSettings.length) {
                        dispatch(AdministrationAction.updateSieauParameters(appSettings))
                    } else if (!appSettings.length && !files) {
                        dispatch(ToastrAction.info(i18n.nothingToChange))
                    }
                    setDisabled(true)
                },
            }
    }, [disabled, applicationSettings, files])

    const onChangeSetting = (setting, value) => {
        const stateObj = applicationSettings.filter(e => e.parameter !== setting)
        setApplicationSettings([...stateObj, { parameter: setting, value }])
    }

    return (
        <>
            <Tabs
                value={tab}
                onChange={(e, newValue) => setTab(newValue)}
                variant='fullWidth'
            >
                <Tab label={ i18n.settings } />
                <Tab label={ i18n.security } />
                <Tab label={ i18n.support } />
                <Tab label={ i18n.applicationParameters } />
            </Tabs>
            {tab === SETTINGS_INDEX && (
                <SettingsPanel
                    applicationSettings={applicationSettings}
                    onChangeSettings={onChangeSetting}
                    disabled={{ disabled, active: !disabled }}
                    themeLayers={themeLayers}
                />
            )}
            {tab === SECURITY_INDEX && (
                <SecurityPanel
                    applicationSettings={applicationSettings}
                    onChangeSettings={onChangeSetting}
                    disabled={{ disabled, active: !disabled }}
                />
            )}
            {tab === SUPPORT_INDEX && (
                <SupportPanel
                    applicationSettings={applicationSettings}
                    onChangeSettings={onChangeSetting}
                    disabled={{ disabled, active: !disabled }}
                />
            )}
            {tab === APP_PARAMS_INDEX && (
                <ApplicationParametersPanel
                    applicationSettings={applicationSettings}
                    onChangeSettings={onChangeSetting}
                    disabled={{ disabled, active: !disabled }}
                    files={files}
                    onChangeFiles={v => setFiles(v)}
                />
            )}
        </>
    )
}

SettingApp.propTypes = {
    applicationSettings: PropTypes.arrayOf(PropTypes.instanceOf(SieauParameterDto)),
    sandreCodes: PropTypes.arrayOf(PropTypes.instanceOf(DtoSandreCode)),
    themeLayers: PropTypes.arrayOf(PropTypes.instanceOf(LayerDto)),
    addPublicFiles: PropTypes.func,
    fetchPublicPicture: PropTypes.func,
    menuPicture: PropTypes.instanceOf(DtoPublicPicture),
    loginPicture: PropTypes.instanceOf(DtoPublicPicture),
    deleteFile: PropTypes.func,
    setGenericMeasures: PropTypes.func,
    classes: PropTypes.instanceOf(PropTypes.object),
    setHelpLink: PropTypes.func,
    setTitle: PropTypes.func,
    fetchSandreCodes: PropTypes.func,
    updateSieauParameters: PropTypes.func,
    info: PropTypes.func,
    fetchApplicationSettings: PropTypes.func,
    accountUser: PropTypes.instanceOf(DtoUser),
}

export default SettingApp
